import { PImage } from "../../shared/PImage/PImage";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import {
  LoyaltyOfferGraphic,
  OfferIconSlug,
  OfferIconUi,
} from "../../../utils/loyaltyTypes";
import styles from "../GeneralOfferIcon/GeneralOfferIcon.module.scss";
import {
  FixedDiscountIcon,
  FreeItemIcon,
  MileStone,
  OfferStarIcon,
  PercentageIcon,
  PercentageIcon2,
  offerIconPath101PX,
} from "../Icons/loyalty.icons";

import { DeliveryIcon } from "../Icons/Icons";
import { BogoIcon, BundleIcon } from "../Icons/offerKind.icons";

type LoyaltyGraphicIconProps = {
  graphic: OfferIconUi | LoyaltyOfferGraphic;
  offerUiGraphics?: LoyaltyOfferGraphic[];
  largeIcons?: boolean;
  fallbackIcon?: OfferIconUi;
  style?: React.CSSProperties;
};

const defaultIconGraphic: OfferIconUi = {
  type: "icon",
  value: OfferIconSlug.Gift,
};

export const LoyaltyGraphicIcon = ({
  graphic,
  offerUiGraphics,
  largeIcons,
  fallbackIcon,
  style,
}: LoyaltyGraphicIconProps) => {
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );
  const alternatePercentageIcon = useAppSelector(
    (state) => state.config.restaurantInfo?.config?.alternatePercentageIcon
  );

  const normalizedGraphic =
    (offerUiGraphics && offerUiGraphics.length > 0
      ? offerUiGraphics[0]
      : graphic) ?? defaultIconGraphic;

  if (normalizedGraphic?.type === "image") {
    if (!("entityId" in normalizedGraphic)) {
      const iconToRender = fallbackIcon?.value ?? defaultIconGraphic.value;

      return mapLoyaltyIcon(iconToRender, alternatePercentageIcon);
    }

    const imageUrl = entityImageLookup[normalizedGraphic.entityId]?.itemImage;

    if (!imageUrl || !largeIcons) {
      const iconToRender = fallbackIcon?.value ?? defaultIconGraphic.value;

      return mapLoyaltyIcon(iconToRender, alternatePercentageIcon);
    }

    return (
      <div
        className={styles.img_container}
        style={{ clipPath: `path("${offerIconPath101PX}")` }}
      >
        <PImage
          src={imageUrl}
          alt=""
          fill
          style={{ objectFit: "cover", objectPosition: "center" }}
          className={styles.img}
        />
      </div>
    );
  }

  if (normalizedGraphic.type === "text") {
    return <span style={style}>{normalizedGraphic.value}</span>;
  }

  return mapLoyaltyOfferIconSlug(
    String(normalizedGraphic.value),
    alternatePercentageIcon
  );
};

/** Shared by `LoyaltyGraphicIcon` and `BundleOfferImages` (loyalty circular badges). */
export const mapLoyaltyOfferIconSlug = (
  iconValue: string,
  alternatePercentageIcon?: boolean
): JSX.Element => {
  switch (iconValue) {
    case OfferIconSlug.Percentage:
    case "percentage":
      return alternatePercentageIcon ? <PercentageIcon2 /> : <PercentageIcon />;
    case OfferIconSlug.Money:
    case "money":
    case "fixed":
    case "dollar":
      return <FixedDiscountIcon />;
    case OfferIconSlug.Multiplier:
    case "multiplier":
      return <MileStone hasFullWidth />;
    case OfferIconSlug.Delivery:
    case "delivery":
      // Solid, to carry the same weight as the percentage and money glyphs it
      // sits beside on the offer badge.
      return <DeliveryIcon solid />;
    case OfferIconSlug.Star:
    case "star":
      return <OfferStarIcon />;
    case OfferIconSlug.Bogo:
    case "bogo":
      return <BogoIcon />;
    case OfferIconSlug.Bundle:
    case "bundle":
      return <BundleIcon />;
    case OfferIconSlug.Gift:
    case "gift":
    case "free":
    default:
      return <FreeItemIcon />;
  }
};

const mapLoyaltyIcon = (
  slug: OfferIconSlug,
  alternatePercentageIcon?: boolean
): JSX.Element =>
  mapLoyaltyOfferIconSlug(String(slug), alternatePercentageIcon);
